import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import { Plus, Pencil, Search, KeyRound, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const empty = { name: "", phone: "", email: "", status: "Active", portal_enabled: false, password: "" };

export default function Employees() {
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/employees", { params: { q, status: statusFilter } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [q, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  useEffect(() => { if (params.get("new") === "1") openNew(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const openEdit = (e) => {
    setEditing(e);
    setForm({ name: e.name, phone: e.phone || "", email: e.email || "", status: e.status, portal_enabled: !!e.portal_enabled, password: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.portal_enabled && !editing && !form.password) return toast.error("Set a password to enable portal access");
    if (form.portal_enabled && !form.email) return toast.error("Email is required for portal access");
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    try {
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="heading text-2xl font-extrabold tracking-tight">Employees</h1>
          <p className="text-sm text-muted-foreground">Manage employees and optionally give them portal access.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-add-employee" className="bg-emerald-600 hover:bg-emerald-700"><Plus size={16} className="mr-1" />Add Employee</Button>
      </div>

      <Card className="p-4">
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input placeholder="Search name, phone or email" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="emp-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="emp-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Portal</th><th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.id} className="border-t table-row-hover" data-testid={`emp-row-${e.id}`}>
                  <td className="p-3 font-medium">{e.name}</td>
                  <td className="p-3 mono text-muted-foreground">{e.phone || "—"}</td>
                  <td className="p-3 text-muted-foreground">{e.email || "—"}</td>
                  <td className="p-3"><StatusBadge status={e.status} /></td>
                  <td className="p-3">{e.portal_enabled ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><KeyRound size={12} />Enabled</span> : <span className="text-xs text-muted-foreground">Disabled</span>}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(e)} data-testid={`btn-edit-${e.id}`}><Pencil size={14} className="mr-1" />Edit</Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 ml-1"
                      onClick={() => setConfirmDelete(e)}
                      data-testid={`btn-delete-emp-${e.id}`}
                    ><Trash2 size={14} /></Button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">No employees yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="emp-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" className="mt-1.5" /></div>
            <div><Label>Phone (E.164 for WhatsApp reminders, e.g. +9647701234567)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" className="mt-1.5" /></div>
            <div><Label>Email {form.portal_enabled && "*"}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="emp-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Enable Employee Portal Access</Label>
                  <div className="text-xs text-muted-foreground">Employee can log in at /portal/login to view their own balance and history.</div>
                </div>
                <Switch checked={form.portal_enabled} onCheckedChange={(v) => setForm({ ...form, portal_enabled: v })} data-testid="emp-portal-switch" />
              </div>
              {form.portal_enabled && (
                <div>
                  <Label>{editing ? "New password (leave empty to keep current)" : "Password *"}</Label>
                  <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leave empty to keep current" : "Set a password"} className="mt-1.5 mono" data-testid="emp-password" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="btn-save-emp" className="bg-emerald-600 hover:bg-emerald-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-emp">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this employee?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block font-medium text-slate-900 dark:text-slate-100 mb-2">{confirmDelete?.name}</span>
              This permanently removes the employee. Employees with any historical orders or payments cannot be deleted — set them to <strong>Inactive</strong> instead so their records stay intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/employees/${confirmDelete.id}`);
                  toast.success("Employee deleted");
                  setConfirmDelete(null);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
              }}
              data-testid="btn-confirm-delete-emp"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
